// =====================================================
// LINK PREVIEW DIALOG - Modal de confirmación para vincular
// =====================================================

import { useEffect, useRef } from 'react';
import { Link2, X, Loader2 } from 'lucide-react';

interface ParentToLink {
  type: string;
  name: string;
}

interface LinkPreviewDialogProps {
  isOpen: boolean;
  isLinking: boolean;
  itemName: string;
  itemType: string;
  parentsToLink: ParentToLink[];
  onClose: () => void;
  onConfirm: () => void;
}

// Traducción de tipos
const typeTranslations: Record<string, string> = {
  specialty: 'Especialidad',
  subspecialty: 'Subespecialidad',
  treatmentGroup: 'Grupo de tratamiento',
  medicalAct: 'Acto médico',
};

export function LinkPreviewDialog({
  isOpen,
  isLinking,
  itemName,
  itemType,
  parentsToLink,
  onClose,
  onConfirm,
}: LinkPreviewDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Cerrar con Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLinking) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLinking, onClose]);

  // Bloquear scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const hasParents = parentsToLink.length > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={!isLinking ? onClose : undefined}
      />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={dialogRef}
          className="relative bg-white rounded-xl shadow-xl max-w-lg w-full transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            disabled={isLinking}
            className="absolute top-4 right-4 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="p-6">
            {/* Icon */}
            <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
              <Link2 className="h-6 w-6 text-blue-600" />
            </div>

            {/* Content */}
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                ¿Vincular {itemName}?
              </h3>
              <p className="text-sm text-gray-600">
                {hasParents
                  ? `Para vincular este elemento, también se vincularán sus ${parentsToLink.length} elemento${parentsToLink.length > 1 ? 's' : ''} padre${parentsToLink.length > 1 ? 's' : ''}.`
                  : `Este ${typeTranslations[itemType] || itemType} se vinculará al centro.`}
              </p>
            </div>

            {/* Parents list */}
            {hasParents && (
              <div className="mb-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Se vincularán automáticamente:
                </p>
                <div className="max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-3 space-y-1">
                  {parentsToLink.map((parent, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                      <span className="text-gray-500">
                        {typeTranslations[parent.type] || parent.type}:
                      </span>
                      <span className="font-medium truncate">{parent.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isLinking}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={onConfirm}
                disabled={isLinking}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLinking ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Vinculando...
                  </>
                ) : (
                  'Vincular'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LinkPreviewDialog;
